/**
 * legacyPlanMap — P1-1 (Plan-Katalog-Mismatch).
 *
 * Konten mit einer Alt-Plan-ID aus dem v1-Import (z.B. "premium" — v1 kannte
 * nur ein flaches Premium-Flag, keine Tarifstufen) matchen keinen der 9
 * SPEC-Tarife aus GET /api/v2/subscription/plans (`single`, `starter`,
 * `ideal`, `professional`, `business`, `enterprise`, `enterprisex`,
 * `enterprisexl`, `free`). Ohne Mapping bleibt `currentPlan` auf allen drei
 * betroffenen Seiten (/plans, /account/subscription, /developer-Gate) `null`.
 *
 * Ziel-Tarif fuer den generischen Alt-Namen "premium": das Backend selbst
 * behandelt "legacy site-based premium" bereits als `professional`
 * (Authentication::SubscriptionService#premium?) — dieselbe Zuordnung wird
 * hier fuer die Anzeige uebernommen, EINE zentrale Stelle statt drei
 * unabhaengiger `.find()`-Aufrufe.
 *
 * `professional_new` (ebenfalls hierher gemappt): PremiumPayment::
 * PaymentProcessingService#apply_upgrade_code schreibt bei Krypto-Zahlungen
 * fuer den Tarif "professional" intern `payment.plan = "professional_new"`
 * (payment_processing_service.rb). Ohne diesen Alias zeigte die Anzeige nach
 * einer erfolgreichen HNT-Zahlung "Professional New" statt "Professional",
 * UND der Plan-Vergleich im Checkout-Polling (HntPaymentPane/CryptoCheckout)
 * lief nie auf einen Treffer, weil `sub.plan_id` ("professional_new") nie
 * gleich `plan.id` ("professional") war — der Checkout haengte nach
 * erfolgreicher Zahlung endlos im Warte-Zustand fest.
 *
 * HINWEIS: Dies behebt nur die FE-ANZEIGE (Plan-Name/Badge). Die tatsaechlichen
 * Kontingent-/API-Zugangswerte fuer solche Konten (`hotspot_quota`,
 * `GET /api/v2/account/api_usage`) werden serverseitig aus `user.plan` berechnet
 * (Authentication::SubscriptionService::PLAN_LIMITS bzw. ApiQuotaService::
 * API_QUOTAS) — kennen diese Karten die Alt-ID ebenfalls nicht, liefert das
 * Backend dort weiterhin falsche/gesperrte Werte. Das ist ohne Backend-Aenderung
 * nicht loesbar (siehe dokumentierte Luecke im Task-Ergebnis).
 */
export const LEGACY_PLAN_ALIASES: Record<string, string> = {
  premium: 'professional',
  professional_new: 'professional',
}

/** Alt-ID -> SPEC-Tarif-ID, sonst unveraendert (bereits eine SPEC-ID oder unbekannt). */
export function resolveSpecPlanId(rawPlanId: string | null | undefined): string | null {
  if (!rawPlanId) return null
  return LEGACY_PLAN_ALIASES[rawPlanId.toLowerCase()] ?? rawPlanId
}

/**
 * Findet den Katalog-Eintrag zu einer (ggf. Alt-)Plan-ID: erst direkter Match,
 * dann ueber das Legacy-Mapping. Ersetzt die drei bisherigen unabhaengigen
 * `plans.find((p) => p.id === subscription.plan_id)`-Aufrufe.
 */
export function findCatalogPlan<T extends { id: string }>(
  plans: T[] | null | undefined,
  rawPlanId: string | null | undefined,
): T | null {
  if (!plans || !rawPlanId) return null
  return (
    plans.find((p) => p.id === rawPlanId) ??
    plans.find((p) => p.id === resolveSpecPlanId(rawPlanId)) ??
    null
  )
}
